/**
 * `tender.ts` — taksonomi tender: **bagian paling mudah salah** di seluruh POS (§7).
 *
 * Sumber: `design/SCHEMA-v0.md` §0 (P1–P10) · §2 (tipe logis) · §6 · §7 · §10b R1 · §12 ·
 * `research/VTEC-TEARDOWN.md` §5 · §7 · §8i · `CLAUDE.md` K7 · K10.
 *
 * ⛔ **POS tak boleh memutuskan akun** (§0). Modul ini hanya menyatakan **jenis ekonomi**
 * (`tender_type`) dengan benar; pemetaan ke akun terjadi di ERP (`pos_tender_map`).
 * Karena itu **tidak ada** `economicEffect`/`accountKind` di sini — enum kedua yang isomorfik
 * dengan `TenderType` = sumber kebenaran kedua yang pasti menyimpang.
 *
 * ---
 * ### P2 — kapan boolean haram, kapan halal
 *
 * > Boolean **haram** bila ia memikul klasifikasi ekonomi. Boolean **halal** bila ia
 * > (a) **diturunkan** dari enum wajib dan tak pernah disimpan berdampingan dengannya, atau
 * > (b) atribut **operasional** satu metode bayar yang hanya punya satu makna.
 *
 * - **Wajib enum:** `tender_type` — satu-satunya. Tak ada `isCash`/`isRevenue`/`asItemDiscount`/
 *   `issale` yang boleh disimpan di baris tender.
 * - **Boleh boolean (operasional, per tenant):** `opensDrawer`, `requiresReference` — hidup di
 *   master {@link PaymentMethodSpec}, bukan di baris transaksi.
 * - **Turunan, tak pernah disimpan:** {@link TENDER_TYPE_TRAITS} — tabel beku, tak dapat
 *   dikonfigurasi tenant.
 *
 * ---
 * ### Catatan silang yang SENGAJA tidak diotomatiskan
 *
 * Tender `non_tender` (komplimen/makan karyawan) dan `exempt_reason` per baris (§8, `tax.ts`)
 * **berkorelasi tapi bukan implikasi**: satu bill boleh dibayar sebagian tunai dan sebagian
 * `non_tender`, sementara pengecualian pajak ditentukan **per baris**, bukan per tender.
 * Menurunkan yang satu dari yang lain akan salah pada kasus campuran ⇒ `tender.ts` **tidak
 * mengimpor** `tax.ts` dan sebaliknya. Validasi silang (kalau nanti perlu) milik lapisan
 * pesanan, bukan kontrak.
 *
 * ---
 * ### Catatan integrasi
 *
 * `Violation` & `Result` di bawah adalah bentuk lintas-modul (C7). Bila modul kontrak lain
 * mendefinisikan nama yang sama, pindahkan keduanya ke `result.ts` bersama dan re-export —
 * `export *` ganda atas nama yang sama membuat `index.ts` ambigu.
 */
function ok(value) {
    return { ok: true, value };
}
function err(code, path, message) {
    return { ok: false, violations: [{ code, path, message }] };
}
/* ────────────────────────────────────────────────────────────────────────────
 * §7 — TenderType: lima nilai, semuanya beda perlakuan akuntansi
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Lima jenis ekonomi tender (§7). **Union string literal, bukan `enum` TypeScript** (C1):
 * `enum` menghasilkan objek runtime + reverse-mapping, sementara union string ter-serialisasi
 * apa adanya ke wire dengan nol biaya runtime.
 *
 * ⚠️ **Nilai literalnya beku selamanya** (C4). Menambah nilai = bump
 * {@link TENDER_TAXONOMY_VERSION} + `CONTRACT_VERSION` **minor**, dan ketiga lapis
 * (portal ↔ outlet ↔ klien) wajib memperlakukan nilai tak dikenal sebagai **error**.
 *
 * | Nilai | Makna ekonomi | Contoh nyata |
 * |---|---|---|
 * | `cash` | uang fisik masuk laci sekarang | tunai |
 * | `clearing` | piutang ke penyelenggara, jeda settlement T+1/T+2 | QRIS · kartu · GoFood · GrabFood · Sodexo |
 * | `liability_release` | sudah dibayar lebih dulu ⇒ melepas liabilitas | voucher · gift card · saldo prepaid · DP event |
 * | `non_tender` | tak ada uang yang akan datang ⇒ beban | komplimen · makan karyawan · promo 100% |
 * | `receivable` | tamu belum bayar ⇒ piutang, ada risiko kredit | bon · member tab · corporate account |
 *
 * **Bukti kenapa lima, bukan satu boolean (§7, `CLAUDE.md` §Gotcha):** `asItemDiscount` Arena
 * mencampur **beban** (DUTY MEAL Rp12,9 jt) + **liabilitas** (PREPAID CREDIT Rp27,9 M) +
 * **piutang pihak ketiga** (GRAB VOUCHER Rp9,54 M) dalam satu kolom ya/tidak.
 *
 * **Bukti empiris kedua (teardown §7):** `ref_paytype_class` (137 baris) di schema
 * `command_center` Arena — mereka **membangun ulang klasifikasi ini di hilir** karena POS-nya
 * tak punya. Di sini ia jadi atribut kelas satu di master, bukan tabel pemetaan di DW.
 *
 * **Gap yang ditutup (§7):** percobaan lama `osbhqpos` memakai `type String // CASH|CARD|QRIS`
 * — itu klasifikasi **alat bayar**, bukan **perlakuan ekonomi**.
 */
export const TENDER_TYPES = Object.freeze([
    'cash',
    'clearing',
    'liability_release',
    'non_tender',
    'receivable',
]);
/**
 * Versi taksonomi tender. **Bukan hiasan:** klasifikasi bersifat historis — kalau makna sebuah
 * trait berubah atau nilai baru ditambahkan, baris lama tetap harus dibaca di bawah tabel lama
 * (§1.5 konsekuensi 4). Bump di sini **wajib** disertai bump `CONTRACT_VERSION` (C4).
 */
export const TENDER_TAXONOMY_VERSION = 2;
/**
 * Guard — satu-satunya pintu masuk data luar untuk `TenderType` (C2, P2 struktural).
 * Case-sensitive: `'CASH'` **ditolak**, karena nilai wire beku apa adanya (C4).
 */
export function isTenderType(v) {
    return typeof v === 'string' && TENDER_TYPES.includes(v);
}
/**
 * Parser `TenderType`. **Tanpa default** (C3) — `'qris'`, `'card'`, `'CASH'` semuanya gagal.
 * `'qris'` adalah alat bayar, bukan jenis ekonomi; ia `clearing`, dan yang memetakannya adalah
 * master `payment_method`, bukan tebakan parser.
 */
export function parseTenderType(v, path = 'tenderType') {
    if (isTenderType(v))
        return ok(v);
    return err('T-TENDER-TYPE-UNKNOWN', path, `Nilai tender_type tidak dikenal. Yang sah: ${TENDER_TYPES.join(' · ')}.`);
}
/* ────────────────────────────────────────────────────────────────────────────
 * §7 / K7 — seam EDC
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Mode EDC (§7, K7). `manual` = kasir mengetik ulang hasil dari mesin EDC;
 * `integrated` = nilainya datang dari device-agent.
 *
 * **Bukti kenapa seam-nya wajib ada di v1 (teardown §8i):** seluruh modul EDC VTEC **nol
 * transaksi** — `edc_paymentinfo`, `edc_voidtransaction`, `edc_mappingbankdetail` semuanya 0 baris
 * — sementara masternya `EDCTypeID 1 = 'Manual EDC'`. Artinya nominal diketik **dua kali**
 * (di mesin EDC dan di POS), dua peluang salah, tak ada yang menjamin keduanya sama; inilah
 * sumber sebagian besar selisih setoran kasir yang "misterius".
 * Kolomnya wajib ada sejak v1 karena **menambahkannya belakangan = membongkar baris transaksi**.
 */
export const EDC_MODES = Object.freeze(['manual', 'integrated']);
/** Guard `EdcMode` (C2). */
export function isEdcMode(v) {
    return typeof v === 'string' && EDC_MODES.includes(v);
}
/** Parser `EdcMode` — tanpa default (C3). */
export function parseEdcMode(v, path = 'edcMode') {
    if (isEdcMode(v))
        return ok(v);
    return err('T-EDC-MODE-UNKNOWN', path, `Nilai edc_mode tidak dikenal. Yang sah: ${EDC_MODES.join(' · ')}.`);
}
const CARD_LAST4_RE = /^[0-9]{4}$/;
const APPROVAL_CODE_RE = /^[A-Za-z0-9]{1,20}$/;
/** Guard `CardLast4` — persis 4 digit, tanpa spasi, tanpa huruf. */
export function isCardLast4(v) {
    return typeof v === 'string' && CARD_LAST4_RE.test(v);
}
/** Parser `CardLast4` — `'12345'`, `'12a4'`, `'1234 '` semuanya gagal (teardown §5, PCI). */
export function parseCardLast4(v, path = 'cardLast4') {
    if (isCardLast4(v))
        return ok(v);
    return err('T-CARD-LAST4-FORMAT', path, 'card_last4 wajib persis 4 digit (0-9). Nomor kartu penuh DILARANG disimpan.');
}
/**
 * Kunci yang **dilarang muncul** di mana pun dalam payload tender. Bentuk ternormalisasi
 * (huruf kecil, tanpa non-alfanumerik) supaya `creditCardNo`, `credit_card_no`, dan
 * `CreditCardNO` tertangkap semuanya. Daftarnya mengikuti kolom nyata VTEC (teardown §5)
 * ditambah varian yang lazim.
 */
const FORBIDDEN_CARD_KEYS = new Set([
    'pan',
    'cardnumber',
    'cardno',
    'creditcardno',
    'creditcardnumber',
    'cardholdername',
    'creditcardholdername',
    'expiremonth',
    'expireyear',
    'expirydate',
    'expirationdate',
    'cvv',
    'cvc',
    'cvv2',
    'track1',
    'track2',
    'magstripe',
]);
function normalizeKey(key) {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}
/**
 * Kontrol yang **MEMAKSA** (P6), bukan komentar: menolak payload apa pun yang memuat kunci data
 * kartu penuh, atau `cardLast4` yang bukan tepat 4 digit.
 *
 * Ditulis sebagai fungsi — bukan sekadar "jangan tambahkan kolomnya" — karena satu-satunya
 * kontrol yang bertahan melewati pergantian orang adalah yang **dieksekusi**. Komentar tak
 * pernah menghentikan siapa pun (teardown §5; §5b `DESIGN-order-lifecycle.md`: kontrol Arena
 * mencatat tanpa mencegah).
 *
 * ⚠️ Pesan pelanggaran **tak pernah menyalin nilainya** — sekali data kartu masuk log, ia sudah
 * bocor terlepas dari apakah baris tendernya jadi tersimpan.
 */
export function assertNoPan(input) {
    const violations = [];
    const seen = new WeakSet();
    const walk = (node, path, depth) => {
        if (depth > 8 || node === null || typeof node !== 'object')
            return;
        if (seen.has(node))
            return;
        seen.add(node);
        if (Array.isArray(node)) {
            for (let i = 0; i < node.length; i += 1) {
                walk(node[i], `${path}[${i}]`, depth + 1);
            }
            return;
        }
        for (const [key, value] of Object.entries(node)) {
            const childPath = path === '' ? key : `${path}.${key}`;
            const norm = normalizeKey(key);
            if (FORBIDDEN_CARD_KEYS.has(norm)) {
                violations.push({
                    code: 'T-PAN-FORBIDDEN',
                    path: childPath,
                    message: `Kunci \`${key}\` dilarang: kontrak ini tak pernah menampung nomor kartu, nama ` +
                        'pemegang, atau masa berlaku (teardown §5 — PCI-DSS). Cukup card_last4 + approval_code.',
                });
                // Sengaja TIDAK menelusuri isinya — jangan pernah menyentuh nilainya.
                continue;
            }
            if (norm === 'cardlast4' && value !== null && !isCardLast4(value)) {
                violations.push({
                    code: 'T-CARD-LAST4-FORMAT',
                    path: childPath,
                    message: 'card_last4 wajib persis 4 digit (0-9) — lebih dari itu berarti PAN parsial.',
                });
                continue;
            }
            walk(value, childPath, depth + 1);
        }
    };
    walk(input, '', 0);
    return violations;
}
/* ────────────────────────────────────────────────────────────────────────────
 * §7 — sifat TURUNAN dari tender_type (tabel beku, bukan setelan tenant)
 * ──────────────────────────────────────────────────────────────────────────── */
/** Kapan uangnya benar-benar masuk. `deferred` = ada jeda settlement (T+1/T+2) atau penagihan. */
export const CASH_INFLOW_TIMINGS = Object.freeze(['immediate', 'deferred', 'none']);
/** Guard `CashInflowTiming` (C2). */
export function isCashInflowTiming(v) {
    return typeof v === 'string' && CASH_INFLOW_TIMINGS.includes(v);
}
/**
 * Boleh-tidaknya sebuah tender melebihi sisa tagihan (⇒ kembalian) — **tiga nilai, bukan boolean**.
 *
 * ⭐ Nilai ketiga (`undecided`) ada karena §7 *"Invarian yang ditegakkan DB"* hanya menyebut tiga
 * hal: `Σ order_tender.amount = grand_total`, `non_tender` tak boleh masuk laporan kas, dan
 * `mdr ≠ discount`. **Larangan over-tender per jenis tak ada di dokumen mana pun** — untuk
 * `liability_release` (voucher/gift card) ia masih 🔶 menunggu keputusan pemilik.
 *
 * Boolean memaksa memilih antara dua kebohongan: `false` menolak kasus F&B yang lazim (voucher
 * Rp100rb atas tagihan Rp80rb) **di depan tamu** atas dasar aturan yang belum diputuskan, `true`
 * mengaku sudah diputuskan. P4 (perilaku = DATA, bukan asumsi terpahat) menuntut keadaan
 * "belum diputuskan" bisa **dinyatakan**, dan P6 menuntut kontrol hanya memaksa untuk hal yang
 * memang sudah diputuskan.
 *
 * ⇒ {@link validateTender} memancarkan `T-OVERTENDER` **hanya** pada `forbidden`.
 */
export const OVER_TENDER_POLICIES = Object.freeze(['allowed', 'forbidden', 'undecided']);
/** Guard `OverTenderPolicy` (C2). */
export function isOverTenderPolicy(v) {
    return typeof v === 'string' && OVER_TENDER_POLICIES.includes(v);
}
/** Kepada siapa tagihannya tertuju. `provider` = penyelenggara kliring; `customer` = tamu. */
export const SETTLEMENT_COUNTERPARTIES = Object.freeze(['none', 'provider', 'customer']);
/** Guard `SettlementCounterparty` (C2). */
export function isSettlementCounterparty(v) {
    return typeof v === 'string' && SETTLEMENT_COUNTERPARTIES.includes(v);
}
/**
 * Tabel sifat — **beku (C8) dan tidak dapat dikonfigurasi tenant**. Kalau tenant boleh menambal
 * ini, taksonomi ekonomi berhenti jadi taksonomi.
 *
 * ⚠️ Tabel ini **historis**: mengubah makna sebuah trait = bump {@link TENDER_TAXONOMY_VERSION}
 * + `CONTRACT_VERSION`, bukan edit senyap — baris lama tetap diklasifikasi di bawah tabel lama
 * (§1.5 konsekuensi 4).
 *
 * | trait | cash | clearing | liability_release | non_tender | receivable |
 * |---|---|---|---|---|---|
 * | `countsAsPhysicalCash` | ✅ | ❌ | ❌ | ❌ | ❌ |
 * | `cashInflowTiming` | immediate | deferred | none | none | deferred |
 * | `settlementCounterparty` | none | provider | none | none | customer |
 * | `requiresSettlementProvider` | ❌ | ✅ | ❌ | ❌ | ❌ |
 * | `requiresCreditControl` | ❌ | ❌ | ❌ | ❌ | ✅ |
 * | `overTenderPolicy` | allowed | forbidden | **undecided** 🔶 | forbidden | forbidden |
 */
/**
 * Membekukan satu baris sifat (C8). Ditulis sebagai fungsi ber-parameter bertipe supaya tiap
 * literal di bawah **dicek lengkap oleh tsc** — bukan disimpulkan longgar jadi `string`.
 */
function frozenTraits(t) {
    return Object.freeze(t);
}
export const TENDER_TYPE_TRAITS = Object.freeze({
    cash: frozenTraits({
        countsAsPhysicalCash: true,
        cashInflowTiming: 'immediate',
        settlementCounterparty: 'none',
        requiresSettlementProvider: false,
        requiresCreditControl: false,
        // Kembalian tunai — satu-satunya jenis yang kembaliannya jelas berupa uang laci.
        overTenderPolicy: 'allowed',
    }),
    clearing: frozenTraits({
        countsAsPhysicalCash: false,
        cashInflowTiming: 'deferred',
        settlementCounterparty: 'provider',
        requiresSettlementProvider: true,
        requiresCreditControl: false,
        // Acquirer menagihkan tepat sebesar transaksinya; lebih dari itu tak punya lawan transaksi.
        overTenderPolicy: 'forbidden',
    }),
    liability_release: frozenTraits({
        countsAsPhysicalCash: false,
        cashInflowTiming: 'none',
        settlementCounterparty: 'none',
        requiresSettlementProvider: false,
        requiresCreditControl: false,
        // 🔶 BELUM DIPUTUSKAN — voucher Rp100rb atas tagihan Rp80rb adalah kasus F&B yang lazim, dan
        // tak ada pasal §7 yang melarangnya. Sampai pemilik memutuskan, kontrak TIDAK memblokirnya
        // (P4/P6). Lihat OVER_TENDER_POLICIES.
        overTenderPolicy: 'undecided',
    }),
    non_tender: frozenTraits({
        countsAsPhysicalCash: false,
        cashInflowTiming: 'none',
        settlementCounterparty: 'none',
        requiresSettlementProvider: false,
        requiresCreditControl: false,
        // Pemberian cuma-cuma melebihi tagihan = beban yang diciptakan dari ketiadaan.
        overTenderPolicy: 'forbidden',
    }),
    receivable: frozenTraits({
        countsAsPhysicalCash: false,
        cashInflowTiming: 'deferred',
        settlementCounterparty: 'customer',
        requiresSettlementProvider: false,
        // Bon member / corporate account — R1 §10b.
        requiresCreditControl: true,
        // Piutang melebihi tagihan = memberi uang kepada tamu yang belum membayar apa pun.
        overTenderPolicy: 'forbidden',
    }),
});
/**
 * Ambil sifat dari **nilai snapshot** (§1.5 konsekuensi 5). Sengaja menerima `TenderType`,
 * **bukan** `PaymentMethodSpec` — supaya pemanggil terpaksa melewati snapshot dan tak tergoda
 * membaca ulang master saat melaporkan.
 */
export function tenderTypeTraits(t) {
    return TENDER_TYPE_TRAITS[t];
}
/** Hanya `cash` yang `true` — invarian §7 *"`non_tender` TIDAK boleh masuk laporan kas"*. */
export function countsAsPhysicalCash(t) {
    return TENDER_TYPE_TRAITS[t].countsAsPhysicalCash;
}
/** `clearing` ⇒ wajib punya penyelenggara, kalau tidak ia tak akan bisa direkonsiliasi (P8). */
export function requiresSettlementProvider(t) {
    return TENDER_TYPE_TRAITS[t].requiresSettlementProvider;
}
/** `receivable` ⇒ wajib lewat kontrol plafon kredit (R1 §10b — dari salinan tersinkron). */
export function requiresCreditControl(t) {
    return TENDER_TYPE_TRAITS[t].requiresCreditControl;
}
/**
 * Kebijakan over-tender sebuah jenis. 🔶 `liability_release` masih `undecided` — dan kontrak
 * **tidak memblokir** apa yang belum diputuskan (P4). Lihat {@link OVER_TENDER_POLICIES}.
 *
 * ⚠️ Sengaja mengembalikan enum, **bukan boolean**: boolean memaksa `undecided` menyamar jadi
 * salah satu jawaban, dan pemanggil kehilangan satu-satunya cara membedakan *"dilarang"* dari
 * *"belum ada yang memutuskan"*.
 */
export function overTenderPolicy(t) {
    return TENDER_TYPE_TRAITS[t].overTenderPolicy;
}
/**
 * **Satu-satunya jalur sah master → transaksi** (§7, §1.5 konsekuensi 2).
 *
 * Menyalin nilai, bukan menyimpan rujukan: setelah dipanggil, perubahan pada master **tidak
 * berpengaruh** pada baris tender yang sudah terbentuk.
 */
export function snapshotTenderType(m) {
    return m.tenderType;
}
/* ────────────────────────────────────────────────────────────────────────────
 * §7 §1.6 — invarian
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Daftar kode pelanggaran modul ini. Beku seperti nilai wire (C4) — kode dipakai di UI, alarm,
 * dan analitik kontrol; mengganti kodenya diam-diam membuat riwayat tak terbaca.
 */
export const TENDER_VIOLATION_CODES = [
    'T-SNAPSHOT-PRESENT',
    'T-AMOUNT-POSITIVE',
    'T-AMOUNT-NOT-EXACT-DECIMAL',
    'T-METHOD-UNKNOWN',
    'T-METHOD-MISMATCH',
    'T-CLEARING-PROVIDER',
    'T-REF-REQUIRED',
    'T-EDC-INTEGRATED-APPROVAL',
    'T-APPROVAL-CODE-FORMAT',
    'T-CARD-LAST4-FORMAT',
    'T-PAN-FORBIDDEN',
    'T-MDR-NOT-DISCOUNT',
    'T-OVERTENDER',
    'T-SUM-EQ-TOTAL',
    'T-TENDER-TYPE-UNKNOWN',
    'T-EDC-MODE-UNKNOWN',
];
const ZERO = 0n;
function isBlank(v) {
    return v === null || v.trim() === '';
}
/**
 * Membuang pelanggaran kembar (kode + path sama) — mis. `card_last4` diperiksa dua kali:
 * sekali sebagai field bertipe, sekali oleh {@link assertNoPan} yang menyisir payload mentah.
 * Dua pintu masuk itu memang disengaja; yang tak disengaja hanyalah pesannya muncul dobel.
 */
function dedupe(list) {
    const seen = new Set();
    const out = [];
    for (const item of list) {
        const key = `${item.code}::${item.path}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(item);
    }
    return out;
}
/**
 * Memeriksa satu baris tender terhadap invarian §1.6. **Tak melempar** — mengembalikan daftar
 * pelanggaran; pemanggil WAJIB gagal-tutup bila tak kosong (C7, P6).
 *
 * Yang diperiksa: `T-METHOD-MISMATCH` · `T-SNAPSHOT-PRESENT` · `T-AMOUNT-NOT-EXACT-DECIMAL` ·
 * `T-AMOUNT-POSITIVE` · `T-CLEARING-PROVIDER` · `T-REF-REQUIRED` · `T-EDC-INTEGRATED-APPROVAL` ·
 * `T-APPROVAL-CODE-FORMAT` · `T-CARD-LAST4-FORMAT` · `T-PAN-FORBIDDEN` · `T-MDR-NOT-DISCOUNT` ·
 * `T-OVERTENDER`.
 *
 * ### 🔶 Yang SENGAJA tidak diperiksa sampai pemilik memutuskan
 * **`edc_mode` terisi pada tender non-`clearing`.** Dugaannya masuk akal (EDC menghasilkan piutang
 * ke acquirer ⇒ semestinya `clearing`), tapi §7/K7 mendefinisikan `edc_mode` sebagai kolom baris
 * tender **tanpa satu pun batasan jenis**, dan tak ada bukti dokumen bahwa tak ada tenant yang
 * menggesek EDC untuk hal lain (mis. voucher `liability_release`). Memancarkannya sebagai
 * `Violation` berarti menolak transaksi di depan tamu atas dasar keputusan yang belum pernah
 * diambil — P4 (perilaku = DATA, bukan asumsi terpahat) melarangnya, dan kepala modul ini
 * mewajibkan pemanggil gagal-tutup atas **setiap** pelanggaran. ⇒ aturannya dicabut, kodenya
 * **tidak** dibekukan di {@link TENDER_VIOLATION_CODES}; tambahkan kembali bila pemilik memutuskan.
 */
export function validateTender(input, ctx, path = 'tender') {
    const v = [];
    // ── T-METHOD-MISMATCH (P6 + P8) — master yang dioper WAJIB milik baris ini.
    // `PaymentMethodSpec.id` dan `paymentMethodId` sama-sama `string` longgar, jadi tsc tak menolong:
    // master keliru membuat SELURUH kontrol di bawah (T-REF-REQUIRED, T-CLEARING-PROVIDER,
    // T-MDR-NOT-DISCOUNT) lolos diam-diam — kesimpulan "tender ini sah" diterbitkan di atas masukan
    // yang bukan miliknya.
    if (ctx.method.id !== input.paymentMethodId) {
        v.push({
            code: 'T-METHOD-MISMATCH',
            path: `${path}.paymentMethodId`,
            message: `Master payment_method '${ctx.method.code}' bukan milik baris tender ini. Validasi ` +
                'dihentikan: jangan terbitkan kesimpulan di atas masukan yang bukan miliknya (P8).',
        });
        // PAN tetap disisir — kontrol PCI tak bergantung pada master dan tak boleh bisa dilewati.
        for (const pan of assertNoPan(input))
            v.push({ ...pan, path: `${path}.${pan.path}` });
        return dedupe(v);
    }
    // ── T-SNAPSHOT-PRESENT (§7) — tanpa snapshot, baris ini tak punya makna ekonomi sama sekali.
    if (!isTenderType(input.tenderTypeSnapshot)) {
        v.push({
            code: 'T-SNAPSHOT-PRESENT',
            path: `${path}.tenderTypeSnapshot`,
            message: 'tender_type_snapshot wajib ada & sah. Tanpa itu, baris tender tak punya klasifikasi ' +
                'ekonomi dan ERP tak bisa memetakannya ke akun (§7).',
        });
        // Sisa pemeriksaan bergantung pada snapshot ⇒ berhenti (P8: jangan menyimpulkan tanpa masukan).
        return v;
    }
    const traits = TENDER_TYPE_TRAITS[input.tenderTypeSnapshot];
    // ── T-AMOUNT-NOT-EXACT-DECIMAL (P1) — `Money`/`Rate` = bigint skala tetap, TAK PERNAH float.
    // Baris yang datang dari kawat lalu di-cast ke OrderTenderInput tak dijaga tipe sama sekali:
    // `amount: 12345.67` (number) membuat SELURUH perbandingan di bawah menjadi campuran
    // number↔bigint yang diam-diam benar sebagian, memicu T-MDR-NOT-DISCOUNT palsu (`0 !== 0n`),
    // dan meledak jadi TypeError di `sumTenderAmounts`. `tax.ts` sudah menutup lubang yang sama
    // lewat TAX-ROW-NOT-EXACT-DECIMAL; di sini pun ia wajib jadi pelanggaran, bukan crash (C7).
    const amountExact = typeof input.amount === 'bigint';
    const mdrExact = typeof ctx.method.mdrPercent === 'bigint';
    if (!amountExact) {
        v.push({
            code: 'T-AMOUNT-NOT-EXACT-DECIMAL',
            path: `${path}.amount`,
            message: 'amount wajib desimal eksak skala tetap (bigint MONEY). Angka JS (float) dilarang: ' +
                'uang tak pernah float, dan di kawat bentuknya string desimal kanonik (P1).',
        });
    }
    if (!mdrExact) {
        v.push({
            code: 'T-AMOUNT-NOT-EXACT-DECIMAL',
            path: `${path}.paymentMethodId`,
            message: `payment_method '${ctx.method.code}' punya mdr_percent yang bukan desimal eksak skala ` +
                'tetap (bigint RATE). Perbandingan uang/tarif selalu eksak, tak pernah float (P1).',
        });
    }
    if (!amountExact || !mdrExact) {
        // Sisa pemeriksaan membandingkan nominal ⇒ berhenti (P8: jangan menyimpulkan di atas
        // masukan yang sudah rusak). PAN tetap disisir — kontrolnya tak bergantung pada nominal.
        for (const pan of assertNoPan(input))
            v.push({ ...pan, path: `${path}.${pan.path}` });
        return dedupe(v);
    }
    // ── T-AMOUNT-POSITIVE (P5) — pembalikan memakai is_reversed, bukan nominal negatif.
    if (input.amount <= ZERO) {
        v.push({
            code: 'T-AMOUNT-POSITIVE',
            path: `${path}.amount`,
            message: 'amount wajib > 0. Pembalikan dicatat lewat baris/penanda is_reversed, bukan nominal ' +
                'negatif — jejak append-only tak boleh dihapus dengan tanda minus (P5).',
        });
    }
    // ── T-CLEARING-PROVIDER (§7 + P8) — clearing tanpa penyelenggara tak akan bisa direkonsiliasi.
    if (traits.requiresSettlementProvider && isBlank(ctx.method.settlementProvider)) {
        v.push({
            code: 'T-CLEARING-PROVIDER',
            path: `${path}.paymentMethodId`,
            message: `Tender '${input.tenderTypeSnapshot}' wajib punya settlement_provider di master ` +
                `payment_method '${ctx.method.code}'. Tanpa itu piutangnya tak akan pernah bisa ` +
                'direkonsiliasi ⇒ tolak sekarang, jangan simpan lalu berharap (P8).',
        });
    }
    // ── T-REF-REQUIRED (§7) — dibandingkan setelah trim; spasi bukan nomor acuan.
    if (ctx.method.requiresReference && isBlank(input.referenceNo)) {
        v.push({
            code: 'T-REF-REQUIRED',
            path: `${path}.referenceNo`,
            message: `payment_method '${ctx.method.code}' menuntut reference_no. Nilai kosong atau hanya ` +
                'spasi tidak sah.',
        });
    }
    // ── Seam EDC (K7, teardown §8i).
    const { edc } = input;
    if (edc.edcMode !== null && !isEdcMode(edc.edcMode)) {
        v.push({
            code: 'T-EDC-MODE-UNKNOWN',
            path: `${path}.edc.edcMode`,
            message: `edc_mode tidak dikenal. Yang sah: ${EDC_MODES.join(' · ')} atau null.`,
        });
    }
    if (edc.edcMode === 'integrated' && isBlank(edc.approvalCode)) {
        v.push({
            code: 'T-EDC-INTEGRATED-APPROVAL',
            path: `${path}.edc.approvalCode`,
            message: 'edc_mode=integrated wajib membawa approval_code, dan nilainya HARUS berasal dari ' +
                'device-agent — tak pernah diketik. Pengetikan ulang adalah sumber selisih setoran ' +
                'kasir yang sedang kita hapus (teardown §8i).',
        });
    }
    if (edc.approvalCode !== null && !APPROVAL_CODE_RE.test(edc.approvalCode)) {
        v.push({
            code: 'T-APPROVAL-CODE-FORMAT',
            path: `${path}.edc.approvalCode`,
            message: 'approval_code wajib 1–20 karakter alfanumerik.',
        });
    }
    // 🔶 Di sinilah dulu `T-EDC-CLEARING-ONLY` berdiri. Dicabut: aturan yang komentarnya sendiri
    // menyatakan "USULAN, BUTUH KONFIRMASI PEMILIK" tak boleh memblokir transaksi (P4/P6) —
    // penjelasan lengkap di JSDoc fungsi ini.
    if (edc.cardLast4 !== null && !isCardLast4(edc.cardLast4)) {
        v.push({
            code: 'T-CARD-LAST4-FORMAT',
            path: `${path}.edc.cardLast4`,
            message: 'card_last4 wajib persis 4 digit (0-9). Nomor kartu penuh DILARANG (teardown §5).',
        });
    }
    // ⛔ PAN tak boleh menyusup lewat field tambahan yang tak dikenal tipe (data dari wire).
    for (const pan of assertNoPan(input)) {
        v.push({ ...pan, path: `${path}.${pan.path}` });
    }
    // ── T-MDR-NOT-DISCOUNT (P3, §7). MDR tak pernah mengurangi amount — modul ini memang tak
    // menyentuh amount sama sekali. Yang bisa diperiksa di sini: non_tender tak punya acquirer,
    // jadi mdr > 0 pasti salah pasang.
    if (input.tenderTypeSnapshot === 'non_tender' && ctx.method.mdrPercent !== ZERO) {
        v.push({
            code: 'T-MDR-NOT-DISCOUNT',
            path: `${path}.paymentMethodId`,
            message: `payment_method '${ctx.method.code}' bertipe non_tender tapi punya mdr_percent ≠ 0. ` +
                'Tak ada acquirer pada pemberian cuma-cuma; MDR adalah BEBAN acquirer, bukan diskon (P3).',
        });
    }
    // ── T-OVERTENDER (§7, P6) — HANYA untuk jenis yang kebijakannya memang sudah diputuskan.
    // `undecided` (liability_release 🔶) sengaja tidak diblokir: menolak voucher Rp100rb atas
    // tagihan Rp80rb di depan tamu, atas dasar trait yang belum dikonfirmasi pemilik, adalah
    // asumsi terpahat — bukan invarian §7 (P4).
    if (traits.overTenderPolicy === 'forbidden' && input.amount > ctx.remainingBalance) {
        v.push({
            code: 'T-OVERTENDER',
            path: `${path}.amount`,
            message: `Tender '${input.tenderTypeSnapshot}' melebihi sisa tagihan, padahal jenis ini tak ` +
                'menghasilkan uang kembali (kembalian hanya sah untuk cash).',
        });
    }
    return dedupe(v);
}
/**
 * Menjumlahkan nominal tender **sebagai bilangan bulat skala tetap**.
 *
 * ⚠️ Ini satu-satunya aritmetika di modul ini, dan ia ada karena invarian `T-SUM-EQ-TOTAL`
 * menuntutnya (§7). Tak ada pembulatan, tak ada konversi skala, tak ada parsing dari string —
 * semua itu milik `money.ts` (C6). Karena `MONEY` = bigint skala tetap, perbandingannya
 * **eksak**: tak ada epsilon, tak ada float (P1).
 *
 * Baris `is_reversed` **tidak** ikut dijumlahkan — pembalikan membatalkan efeknya tanpa
 * menghapus jejaknya (P5).
 */
export function sumTenderAmounts(tenders) {
    let total = ZERO;
    for (const t of tenders) {
        if (t.isReversed)
            continue;
        total += t.amount;
    }
    return total;
}
/**
 * Menyaring tender yang **benar-benar masuk laci** — dasar Z-report & setoran kasir.
 *
 * Menegakkan §7: `non_tender` TIDAK boleh masuk laporan kas — dan sekaligus `clearing`,
 * `liability_release`, `receivable` juga tidak. Hanya `cash`.
 */
export function filterPhysicalCash(tenders) {
    return tenders.filter((t) => !t.isReversed && countsAsPhysicalCash(t.tenderTypeSnapshot));
}
/** Total uang fisik di laci (§7). Turunan dari {@link filterPhysicalCash}. */
export function sumPhysicalCash(tenders) {
    return sumTenderAmounts(filterPhysicalCash(tenders));
}
/**
 * Memeriksa seluruh tender atas satu pesanan (§1.6).
 *
 * ⚠️ `tenders` diasumsikan **urut kronologis** (`paidAt` menaik) — sisa tagihan dihitung
 * progresif, jadi urutan menentukan tender mana yang dianggap kelebihan.
 *
 * Selain invarian per baris, memeriksa:
 * - `T-METHOD-UNKNOWN` — master tak ditemukan ⇒ **berhenti**, jangan menyimpulkan apa pun dari
 *   ketiadaan (P8).
 * - `T-SUM-EQ-TOTAL` — hanya saat `isSettled`, dibandingkan **eksak** sebagai bigint skala tetap.
 */
export function validateTenderSet(tenders, ctx, path = 'tenders') {
    const v = [];
    let applied = ZERO;
    for (let i = 0; i < tenders.length; i += 1) {
        const t = tenders[i];
        if (t === undefined)
            continue;
        const itemPath = `${path}[${i}]`;
        const method = ctx.methods.get(t.paymentMethodId);
        if (method === undefined) {
            v.push({
                code: 'T-METHOD-UNKNOWN',
                path: `${itemPath}.paymentMethodId`,
                message: 'payment_method tidak ditemukan. Baris tender tak boleh divalidasi (apalagi disimpan) ' +
                    'tanpa masternya — jangan terbitkan kesimpulan bila masukannya tak ada (P8).',
            });
            continue;
        }
        v.push(...validateTender(t, { method, remainingBalance: (ctx.grandTotal - applied) }, itemPath));
        // P1/C7: nominal yang bukan bigint sudah dilaporkan `T-AMOUNT-NOT-EXACT-DECIMAL` di atas.
        // Menjumlahkannya di sini akan melempar `TypeError: Cannot mix BigInt and other types` —
        // dan validator modul ini **tak pernah melempar**. Barisnya dilewati; akibatnya
        // `T-SUM-EQ-TOTAL` ikut menyala, dan itu memang benar (gagal-tutup).
        if (!t.isReversed && typeof t.amount === 'bigint')
            applied += t.amount;
    }
    // ── T-SUM-EQ-TOTAL (§7, P1) — perbandingan EKSAK atas bigint skala tetap.
    if (ctx.isSettled && applied !== ctx.grandTotal) {
        v.push({
            code: 'T-SUM-EQ-TOTAL',
            path,
            message: 'Σ order_tender.amount ≠ order.grand_total pada pesanan lunas. Selisih sekecil satu ' +
                'satuan skala terkecil pun ditolak — perbandingan uang selalu eksak, tak pernah float (P1).',
        });
    }
    return v;
}
//# sourceMappingURL=tender.js.map